import {useRef, useMemo, useCallback} from 'react';
import {Formik, Form, FormikProps, useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import InnerPop from '../../../../component/newPopup/innerPop/InnerPop';
import Input from '../../../../component/input/Input';
import NumberInput from '../../../../component/input/NumberInput';
import {TShow} from '../../../../component/prompotionPopup/PromotionPopup';
import {getPricingGroupValidationSchema} from './PricingGroupPop.validation';
import {PriceGroup} from '../../../../redux/subscription/subscription.type';
import {TState} from '../../../../redux/store.type';
import {XHR_LOADING_STATE} from '../../../../assets/constants/constants';
import * as css from './PricingGroupPop.css';

export interface PricingGroupFormValues {
    name: string;
    startPrice: string;
    endPrice: string;
}

interface PricingGroupPopProps {
    show: TShow;
    onSave?: (values: PricingGroupFormValues) => boolean | void | Promise<boolean | void>;
    initialValues?: Partial<PricingGroupFormValues>;
    priceGroupId?: number | null;
    setPriceGroupId?: (id: number | null) => void;
    priceGroups?: PriceGroup[] | null;
}

function PricingGroupPop({
    show,
    onSave,
    initialValues: propInitialValues,
    priceGroupId,
    setPriceGroupId,
    priceGroups
}: PricingGroupPopProps) {
    const {t} = useTranslation('subscriptions');
    const {t: globalT} = useTranslation('global');
    const formRef = useRef<FormikProps<PricingGroupFormValues>>(null);
    const createPriceGroupXHR = useSelector((state: TState) => state.subscription.createPriceGroupXHR);
    const updatePriceGroupXHR = useSelector((state: TState) => state.subscription.updatePriceGroupXHR);
    const isSaveDisabled = createPriceGroupXHR === XHR_LOADING_STATE || updatePriceGroupXHR === XHR_LOADING_STATE;
    // Get redemptionPrice and redemptionUpToPrice from parent Formik context
    const parentFormik = useFormikContext<any>();
    const redemptionPrice = parentFormik?.values?.redemptionPrice || null;
    const redemptionUpToPrice = parentFormik?.values?.redemptionUpToPrice || null;

    const validationSchema = getPricingGroupValidationSchema(globalT, t);

    const formInitialValues: PricingGroupFormValues = useMemo(() => {
        if(propInitialValues) {
            return {
                name: propInitialValues.name || '',
                startPrice: propInitialValues.startPrice || '',
                endPrice: propInitialValues.endPrice || ''
            };
        }

        if(priceGroupId) {
            const priceGroup = (priceGroups || []).find((pg: PriceGroup) => pg.spgId === priceGroupId);
            if(priceGroup) {
                return {
                    name: priceGroup.title,
                    startPrice: priceGroup.minPrice?.toString() || '',
                    endPrice: priceGroup.maxPrice?.toString() || ''
                };
            }
        }

        return {
            name: '',
            startPrice: redemptionPrice || '',
            endPrice: redemptionUpToPrice || ''
        };
    }, [propInitialValues, priceGroupId, priceGroups, redemptionPrice, redemptionUpToPrice]);

    const handleSave = async (values: PricingGroupFormValues) => {
        if(onSave) {
            const saved = await onSave(values);
            if(saved !== false) {
                formRef.current?.resetForm();
            }
        }
    };

    const handleClose = useCallback(() => {
        formRef.current?.resetForm();
        setPriceGroupId?.(null);
        show.close();
    }, [setPriceGroupId, show]);

    // Wrap show to intercept close (for X button in InnerPop)
    const wrappedShow = useMemo(() => ({
        ...show,
        close: handleClose
    }), [show, handleClose]);

    return (
        <css.pricingGroupPop>
            <Formik
                innerRef={formRef}
                initialValues={formInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSave}
                enableReinitialize
            >
                {({submitForm, isSubmitting}) => {
                    const handleSubmitClick = () => {
                        if(isSubmitting || isSaveDisabled) {
                            return;
                        }
                        void submitForm();
                    };

                    return (
                        <Form placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <InnerPop
                                dataCy='pricingGroup'
                                show={wrappedShow}
                                title={priceGroupId ? t('editPriceGroup') : t('pricingGroup')}
                                buttons={{
                                    whiteBtn: {
                                        text: t('cancel'),
                                        onClick: handleClose,
                                        disabled: isSaveDisabled
                                    },
                                    blueBtn: {
                                        text: t('save'),
                                        onClick: handleSubmitClick,
                                        disabled: isSaveDisabled,
                                        className: isSaveDisabled ? XHR_LOADING_STATE : ''
                                    }
                                }}
                            >
                                <css.header />
                                <css.content>
                                    <Input
                                        name='name'
                                        text={t('pricingGroupName')}
                                        description={t('pricingGroupNameDescription')}
                                        icon={null}
                                        events={{}}
                                        className=''
                                        forwardRef={null}
                                    />
                                    <NumberInput
                                        name='startPrice'
                                        text={t('startPrice')}
                                        icon={null}
                                        className=''
                                        forwardRef={null}
                                    />
                                    <NumberInput
                                        name='endPrice'
                                        text={t('endPrice')}
                                        icon={null}
                                        className=''
                                        forwardRef={null}
                                    />
                                </css.content>
                            </InnerPop>
                        </Form>
                    );
                }}
            </Formik>
        </css.pricingGroupPop>
    );
}

export default PricingGroupPop;

